import {
  Button,
  Datepicker,
  InnerContent,
  SimpleModal,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalTitle,
} from '@fountain/fountain-ui-components';
import { Grid, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { addMessageAction } from 'containers/FlashMessage/actions';
import { makeSelectLocale } from 'containers/LanguageProvider/selectors';
import { usePostHog } from 'hooks/usePostHog';
import globalMessages from 'shared/global/messages';
import { getDateFormatPattern } from 'utils/datePlaceholder';

import { exportAgentReport } from '../../api';
import { messages } from '../../messages';
import { AxAgentResponse } from '../../types';
import { isValidReportDateRange, toLocalDateISOString } from '../../utils';
import { useExportReportModalStyles } from '../styles';

interface ExportAgentReportModalProps {
  onClose: () => void;
  agent?: AxAgentResponse;
  accountSlug: string;
}

export const ExportAgentReportModal: React.FC<ExportAgentReportModalProps> = ({
  onClose,
  agent,
  accountSlug,
}) => {
  const classes = useExportReportModalStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const posthog = usePostHog();
  const locale = useSelector(makeSelectLocale());

  // Default the range to the current month so far: first of the month → today.
  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const today = new Date();
  const hasInvalidRange = Boolean(
    startDate && endDate && !isValidReportDateRange(startDate, endDate),
  );
  const canSubmit = isValidReportDateRange(startDate, endDate) && !isSubmitting;

  const handleCancel = () => {
    posthog.capture('candidate_ai_agent:export_report_cancel', {
      agent_id: agent?.uuid,
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!agent?.uuid || !startDate || !endDate) return;

    setIsSubmitting(true);
    try {
      await exportAgentReport(accountSlug, agent.uuid, {
        startDate: toLocalDateISOString(startDate),
        endDate: toLocalDateISOString(endDate),
      });
      posthog.capture('candidate_ai_agent:export_report_submit', {
        agent_id: agent.uuid,
      });
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.exportReportRequestSuccess),
          'success',
        ),
      );
      onClose();
    } catch (err) {
      // Surface the underlying failure (status/body) instead of swallowing it —
      // the generic flash alone makes report failures impossible to diagnose.
      // eslint-disable-next-line no-console
      console.error('Failed to trigger conversation report export', err);
      posthog.capture('candidate_ai_agent:export_report_fail', {
        agent_id: agent.uuid,
        error: err instanceof Error ? err.message : String(err),
      });
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.exportReportRequestFailed),
          'error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SimpleModal onClose={handleCancel} maxWidth={480} blurOverlay={false}>
      <SimpleModalHeader onClose={handleCancel}>
        <SimpleModalTitle>
          <FormattedMessage {...messages.exportReportModalTitle} />
        </SimpleModalTitle>
      </SimpleModalHeader>
      <InnerContent>
        <Typography variant="body2" className={classes.description}>
          <FormattedMessage
            {...messages.exportReportModalDescription}
            values={{ agentName: agent?.name }}
          />
        </Typography>
        <Grid container className={classes.dateRangeContainer}>
          <div className={classes.dateField}>
            <Typography variant="body2" className={classes.dateLabel}>
              <FormattedMessage {...messages.startDate} />
            </Typography>
            <Datepicker
              maxDate={endDate ?? today}
              locale={locale}
              value={startDate}
              onDateChange={date => setStartDate(date as Date | undefined)}
              renderDate={selectedDate => (
                <FormattedDate dateStyle="short" value={selectedDate} />
              )}
              renderPlaceholder={() => getDateFormatPattern(locale)}
            />
          </div>
          <div className={classes.dateField}>
            <Typography variant="body2" className={classes.dateLabel}>
              <FormattedMessage {...messages.endDate} />
            </Typography>
            <Datepicker
              minDate={startDate}
              maxDate={today}
              locale={locale}
              value={endDate}
              onDateChange={date => setEndDate(date as Date | undefined)}
              renderDate={selectedDate => (
                <FormattedDate dateStyle="short" value={selectedDate} />
              )}
              renderPlaceholder={() => getDateFormatPattern(locale)}
            />
          </div>
        </Grid>
        {hasInvalidRange && (
          <Typography
            variant="caption"
            color="error"
            className={classes.dateRangeError}
          >
            <FormattedMessage {...messages.exportReportDateRangeError} />
          </Typography>
        )}
      </InnerContent>
      <SimpleModalFooter>
        <Button
          type="secondary"
          onClick={handleCancel}
          className={classes.cancelButton}
        >
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        <Button
          submit
          type="primary"
          isLoading={isSubmitting}
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          <FormattedMessage {...messages.emailReport} />
        </Button>
      </SimpleModalFooter>
    </SimpleModal>
  );
};
